import React, { useState, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Stack,
  Typography,
  IconButton,
  Box,
  Chip,
  Divider,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"

const UNCATEGORIZED = "Uncategorized"

function getCategoryKey(rel) {
  return rel.category && rel.category.trim() ? rel.category.trim() : UNCATEGORIZED
}

function RelativeFilterDialog({ open, onClose, relatives, selectedIds, onChange }) {
  const [search, setSearch] = useState("")

  // Group relatives by category for the chip row.
  const categories = useMemo(() => {
    const groups = new Map() // categoryName → array of relative rows
    for (const r of relatives) {
      const key = getCategoryKey(r)
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(r)
    }
    return [...groups.entries()]
      .map(([name, members]) => ({
        name,
        members,
        memberIds: members.map((m) => m.id),
        selectedCount: members.filter((m) => selectedIds.has(m.id)).length,
        total: members.length,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [relatives, selectedIds])

  const filtered = useMemo(() => {
    if (!search) return relatives
    const q = search.toLowerCase()
    return relatives.filter((r) => {
      const fields = [r.name, r.address, r.city, r.state, r.category]
      return fields.some((f) => f && f.toLowerCase().includes(q))
    })
  }, [relatives, search])

  const toggle = (id) => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(next)
  }

  const toggleCategory = (cat) => {
    const next = new Set(selectedIds)
    const allSelected = cat.selectedCount === cat.total
    if (allSelected) {
      for (const id of cat.memberIds) next.delete(id)
    } else {
      for (const id of cat.memberIds) next.add(id)
    }
    onChange(next)
  }

  const selectAll = () => onChange(new Set(relatives.map((r) => r.id)))
  const clearAll = () => onChange(new Set())
  const selectFilteredOnly = () => onChange(new Set(filtered.map((r) => r.id)))

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        Filter people
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {selectedIds.size} of {relatives.length} selected
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {categories.length > 1 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary">
              By category — click to toggle the whole group
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
              {categories.map((cat) => {
                const allOn = cat.selectedCount === cat.total
                const someOn = cat.selectedCount > 0 && !allOn
                return (
                  <Chip
                    key={cat.name}
                    label={`${cat.name} (${cat.selectedCount}/${cat.total})`}
                    onClick={() => toggleCategory(cat)}
                    color={allOn ? "primary" : someOn ? "default" : "default"}
                    variant={allOn ? "filled" : someOn ? "outlined" : "outlined"}
                    sx={{
                      opacity: cat.selectedCount === 0 ? 0.55 : 1,
                      fontWeight: allOn ? 600 : 400,
                    }}
                  />
                )
              })}
            </Box>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name, address, or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button size="small" onClick={selectAll}>Select all</Button>
          <Button size="small" onClick={clearAll}>Clear all</Button>
        </Stack>

        {search && filtered.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Button size="small" onClick={selectFilteredOnly}>
              Select only these {filtered.length}
            </Button>
          </Box>
        )}

        {filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
            No matches.
          </Typography>
        ) : (
          <List dense disablePadding>
            {filtered.map((r) => {
              const checked = selectedIds.has(r.id)
              const cat = getCategoryKey(r)
              const secondaryParts = []
              if (r.address) secondaryParts.push(r.address)
              const cityState = [r.city, r.state].filter(Boolean).join(", ")
              if (cityState) secondaryParts.push(cityState)
              return (
                <ListItem
                  key={r.id}
                  disablePadding
                  secondaryAction={
                    <Chip size="small" label={cat} variant="outlined" sx={{ ml: 1 }} />
                  }
                >
                  <ListItemButton onClick={() => toggle(r.id)} dense>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                    </ListItemIcon>
                    <ListItemText
                      primary={r.name}
                      secondary={secondaryParts.join(" — ") || undefined}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">Done</Button>
      </DialogActions>
    </Dialog>
  )
}

export default RelativeFilterDialog
